import { getTodayInUserTimezone } from '@/lib/utils/timezone';

export interface FoodHistoryItem {
  id: string;
  name: string;
  mealType: string;
  timeOfDay: number; // hour of day (0-23)
  frequency: number; // how often user logs this food
  lastLogged: string;
  averageQuantity: number;
  nutritionProfile: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
}

export interface SmartFoodSuggestion {
  food: FoodHistoryItem;
  reason: 'frequent' | 'time_based' | 'macro_goal' | 'recent_favorite';
  confidence: number;
}

export class SmartFoodService {
  private static STORAGE_KEY = 'nutrition_food_history';
  
  // Get user's food history from localStorage (in real app, this would be from database)
  static getFoodHistory(): FoodHistoryItem[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  // Update food history when user logs a meal
  static updateFoodHistory(foodName: string, mealType: string, nutrition: any) {
    const history = this.getFoodHistory();
    const now = new Date();
    const hour = now.getHours();
    const today = getTodayInUserTimezone();

    const existingIndex = history.findIndex(
      item => item.name.toLowerCase() === foodName.toLowerCase() && 
               item.mealType === mealType
    );

    if (existingIndex >= 0) {
      // Update existing entry
      const existing = history[existingIndex];
      existing.frequency += 1;
      existing.lastLogged = today;
      existing.timeOfDay = Math.round((existing.timeOfDay + hour) / 2); // Average time
      existing.averageQuantity = (existing.averageQuantity + (nutrition.quantity || 100)) / 2;
    } else {
      // Add new entry
      const newItem: FoodHistoryItem = {
        id: crypto.randomUUID(),
        name: foodName,
        mealType,
        timeOfDay: hour,
        frequency: 1,
        lastLogged: today,
        averageQuantity: nutrition.quantity || 100,
        nutritionProfile: {
          calories: nutrition.calories || 0,
          protein: nutrition.protein || 0,
          carbs: nutrition.carbs || 0,
          fats: nutrition.fats || 0,
        },
      };
      history.push(newItem);
    }

    // Keep only top 100 most frequent items
    history.sort((a, b) => b.frequency - a.frequency);
    const trimmed = history.slice(0, 100);

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmed));
  }

  // Get smart food suggestions based on current time and patterns
  static getSmartSuggestions(currentMealType: string, targetMacros?: any): SmartFoodSuggestion[] {
    const history = this.getFoodHistory();
    const currentHour = new Date().getHours();
    const suggestions: SmartFoodSuggestion[] = [];

    // Filter foods for current meal type
    const mealFoods = history.filter(item => item.mealType === currentMealType);

    // Time-based suggestions (foods usually eaten at this time)
    mealFoods
      .filter(item => Math.abs(item.timeOfDay - currentHour) <= 2)
      .slice(0, 3)
      .forEach(food => {
        suggestions.push({
          food,
          reason: 'time_based',
          confidence: 0.8 + (food.frequency / 100) * 0.2,
        });
      });

    // Frequency-based suggestions (most logged foods)
    mealFoods
      .filter(item => item.frequency >= 3)
      .slice(0, 3)
      .forEach(food => {
        if (!suggestions.find(s => s.food.id === food.id)) {
          suggestions.push({
            food,
            reason: 'frequent',
            confidence: Math.min(0.9, 0.5 + (food.frequency / 50)),
          });
        }
      });

    // Recent favorites (foods logged in last week)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    mealFoods
      .filter(item => new Date(item.lastLogged) >= oneWeekAgo)
      .slice(0, 2)
      .forEach(food => {
        if (!suggestions.find(s => s.food.id === food.id)) {
          suggestions.push({
            food,
            reason: 'recent_favorite',
            confidence: 0.7,
          });
        }
      });

    // Macro-goal based suggestions (if user needs specific macros)
    if (targetMacros) {
      const macroFoods = mealFoods
        .filter(item => {
          const nutrition = item.nutritionProfile;
          if (targetMacros.needsProtein && nutrition.protein > 15) return true;
          if (targetMacros.needsCarbs && nutrition.carbs > 20) return true;
          if (targetMacros.lowCalorie && nutrition.calories < 200) return true;
          return false;
        })
        .slice(0, 2);

      macroFoods.forEach(food => {
        if (!suggestions.find(s => s.food.id === food.id)) {
          suggestions.push({
            food,
            reason: 'macro_goal',
            confidence: 0.75,
          });
        }
      });
    }

    // Sort by confidence and return top suggestions
    return suggestions
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 6);
  }

  // Get portion size suggestions based on history
  static getSuggestedPortion(foodName: string, mealType: string): number {
    const history = this.getFoodHistory();
    const food = history.find(
      item => item.name.toLowerCase() === foodName.toLowerCase() && 
               item.mealType === mealType
    );
    
    return food?.averageQuantity || 100;
  }

  // Clear history (for testing or user preference)
  static clearHistory() {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Get usage stats for debugging
  static getStats() {
    const history = this.getFoodHistory();
    return {
      totalFoods: history.length,
      totalLogs: history.reduce((sum, item) => sum + item.frequency, 0),
      topFoods: history.slice(0, 5).map(item => ({
        name: item.name,
        frequency: item.frequency,
        mealType: item.mealType,
      })),
    };
  }
}
